import type { Icon } from "@tabler/icons-react";
import { useMemo } from "react";
import { useLocation } from "wouter";

export const UniversalRoutes = {
  HOME: "/",
  PROFILE: {
    HOME: "/profile",
    CREATE: "/profile/create",
    PERSONAL_INFO: "/profile/personal-info",
    NOTIFICATIONS: "/profile/notifications",
    PORTRAIT: "/profile/portrait",
  },
  GROUPS: {
    LIST: "/groups",
    DETAIL: {
      path: "/groups/:groupId",
      params: ["groupId"] as const,
    },
    JOIN: {
      path: "/join/group/:groupId",
      params: ["groupId"] as const,
    },
  },
  SESSIONS: {
    LIST: "/sessions",
    DETAIL: {
      path: "/sessions/:sessionId",
      params: ["sessionId"] as const,
    },
  },
  CHAT: {
    LIST: "/chats",
    DETAIL: {
      path: "/chats/:engagementId",
      params: ["engagementId"] as const,
    },
  },
  GROUP_CHAT: {
    DETAIL: {
      base: "/group-chats/",
      path: "/group-chats/:groupId",
      params: ["groupId"] as const,
    },
  },
  NOTIFICATIONS: {
    LIST: "/notifications",
  },
  FILE_PREVIEW: {
    path: "/file/:fileId",
    params: ["fileId"] as const,
  },
  TRAINER: {
    path: "/trainer/:trainerId",
    params: ["trainerId"] as const,
  },
  TRAINERS: {
    DETAIL: {
      path: "/trainers/:trainerId",
      params: ["trainerId"] as const,
    },
  },
};

export const TrainerRoutes = {
  INVITE: "/invite",
  JOIN_GROUP: {
    path: "/join/group/:groupId",
    params: ["groupId"] as const,
  },
  PROFILE: {
    GALLERY: "/profile/gallery",
    SCHEDULING: "/profile/scheduling",
    SCHEDULES: "/profile/schedules",
    SCHEDULE_EDIT: {
      path: "/profile/schedules/:scheduleId/edit",
      params: ["scheduleId"] as const,
    },
    SESSION_TYPES: "/profile/session-types",
    SESSION_TYPE_EDIT: {
      path: "/profile/session-types/:sessionTypeId",
      params: ["sessionTypeId"] as const,
    },
    GROUP_CLASS_TYPES: "/profile/group-class-types",
    GROUP_CLASS_TYPE_EDIT: {
      path: "/profile/group-class-types/:groupClassTypeId",
      params: ["groupClassTypeId"] as const,
    },
    BOOKING_SETTINGS: "/profile/booking-settings",
    PUBLIC_PROFILE: "/profile/public-profile",
  },
  LIBRARY: {
    LIST: "/library",
    FOLDER: { path: "/library/:folderId", params: ["folderId"] as const },
    UPLOAD: {
      path: "/library/:folderId/upload",
      params: ["folderId"] as const,
    },
    FILE_EDIT: {
      path: "/library/file/:fileId",
      params: ["fileId"] as const,
    },
    FOLDER_EDIT: {
      path: "/library/folder/:folderId",
      params: ["folderId"] as const,
    },
    NEW_TEXT_NOTE: {
      path: "/library/:folderId/text-note/new",
      params: ["folderId"] as const,
    },
    NEW_SURVEY: {
      path: "/library/:folderId/survey/new",
      params: ["folderId"] as const,
    },
    MOVE_TO_FOLDER: {
      path: "/library/move/:itemType/:itemId",
      params: ["itemType", "itemId"] as const,
    },
  },
  CLIENTS: {
    LIST: "/clients",
    DETAIL: {
      path: "/clients/:engagementId",
      params: ["engagementId"] as const,
    },
  },
  SURVEYS: {
    RESULTS: {
      path: "/engagements/:engagementId/surveys/:surveyId",
      params: ["engagementId", "surveyId"] as const,
    },
  },
  CHAT: {
    FILE_PICKER: {
      path: "/chats/:engagementId/files",
      params: ["engagementId"] as const,
    },
    FILE_PREVIEW: {
      path: "/chats/:engagementId/files/:fileId",
      params: ["engagementId", "fileId"] as const,
    },
  },
  GROUP_CHAT: {
    DETAIL: {
      path: "/chats/groups/:groupId",
      params: ["groupId"] as const,
    },
  },
};

export const OwnerRoutes = {
  PROFILE: {
    DOGS: "/profile/dogs",
    DOGS_PORTRAIT: "/profile/dogs-portrait",
  },
  LIST_TRAINERS: "/trainers",
  FIND_TRAINING: "/find-training",
  TRAINERS: {
    INVITE: {
      path: "/join/trainer/:inviteCode/:slug?",
      params: ["inviteCode"] as const,
      optionalParams: ["slug"] as const,
    },
  },
  TRAINER_ENGAGEMENT: {
    path: "/my-trainers/:engagementId",
    params: ["engagementId"] as const,
  },
  REQUEST_SESSION: {
    path: "/trainers/:trainerId/request",
    params: ["trainerId"] as const,
  },
  SURVEYS: {
    FILL: {
      path: "/engagements/:engagementId/surveys/:surveyId",
      params: ["engagementId", "surveyId"] as const,
    },
  },
};

type Route<TParamNames extends string> = {
  path: string;
  params: readonly TParamNames[];
  optionalParams?: readonly string[];
};

type ParamsMap<TParamNames extends string> = {
  [TParamName in TParamNames]: string;
};

const materializeRoute = <TParamNames extends string>(
  route: Route<TParamNames>,
  paramValues: ParamsMap<TParamNames>,
): string => {
  let url = route.path;
  route.params.forEach((param: TParamNames) => {
    if (!paramValues[param]) {
      throw new Error(`Missing value for parameter "${param}"`);
    }
    url = url.replace(`:${param}`, paramValues[param]);
  });
  route.optionalParams?.forEach((param: string) => {
    url = url.replace(`/:${param}?`, "");
  });
  return url;
};

interface ParameterlessRoute {
  path: string;
}

interface ParameterizedRoute<TParamNames extends string> {
  path: Route<TParamNames>;
  params: ParamsMap<TParamNames>;
}

export const createPath = <TParamNames extends string>(
  route: ParameterlessRoute | ParameterizedRoute<TParamNames>,
) => {
  if (typeof route.path === "string") {
    return route.path;
  }

  const parametrizedRout = route as ParameterizedRoute<TParamNames>;
  return materializeRoute(parametrizedRout.path, parametrizedRout.params);
};

export const useNavigator = () => {
  const [, setLocation] = useLocation();
  return useMemo(
    () => ({
      navigator<TParamNames extends string>(
        route: ParameterlessRoute | ParameterizedRoute<TParamNames>,
      ) {
        return () => {
          const path = createPath(route);
          setLocation(path);
        };
      },

      navigate<TParamNames extends string>(
        route: ParameterlessRoute | ParameterizedRoute<TParamNames>,
      ) {
        const path = createPath(route);
        setLocation(path);
      },
    }),
    [setLocation],
  );
};

export type MinArray = MinArrayType<string>;

export type MinArrayType<T> = [T, ...T[]];

export interface NavItem {
  label: string;
  route: string | MinArray;
  icon: Icon;
  selectedIcon?: Icon;
  chat?: boolean;
}
